import GenogramPngChunkHelper from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramPngChunkHelper";
import {
	GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX,
	PNG_DEFAULT_IDAT_CHUNK_SIZE,
	PNG_FILTER_TYPE_NONE
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/export/GenogramExportConstants";

export default class GenogramPngStreamWriter {

	constructor(options = {}) {
		this.chunkHelper = options.chunkHelper || new GenogramPngChunkHelper()
		this.logger = options.logger || null
		this.filterType = options.filterType ?? PNG_FILTER_TYPE_NONE
		this.idatChunkSize = options.idatChunkSize || PNG_DEFAULT_IDAT_CHUNK_SIZE
	}

	async buildBlob({width, height, rowReader}) {
		let startedAt = Date.now()
		this.validateBuildOptions(width, height, rowReader)
		this.logger?.log("PngEncodingStarted", {
			width,
			height,
			filterType: this.filterType,
			idatChunkSize: this.idatChunkSize,
		})
		let compressedBytes = await this.compressRows({width, height, rowReader})
		this.logger?.log("PngRowsCompressed", {
			width,
			height,
			compressedBytes: compressedBytes.byteLength,
			durationMs: Date.now() - startedAt,
		})

		let blob = new Blob([
			this.chunkHelper.getSignatureBytes(),
			this.chunkHelper.createIHDRChunk(width, height),
			...this.chunkHelper.createIDATChunks(compressedBytes, this.idatChunkSize),
			this.chunkHelper.createIENDChunk(),
		], {type: "image/png"})
		this.logger?.log("PngEncodingCompleted", {
			width,
			height,
			blobBytes: blob.size,
			durationMs: Date.now() - startedAt,
		})
		return blob
	}

	validateBuildOptions(width, height, rowReader) {
		if (!Number.isInteger(width) || width <= 0) {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} Invalid PNG width: ${width}`)
		}

		if (!Number.isInteger(height) || height <= 0) {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} Invalid PNG height: ${height}`)
		}

		if (!rowReader || typeof rowReader.getRow !== "function") {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} PNG rowReader.getRow(rowIndex) is required`)
		}

		if (typeof CompressionStream !== "function") {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} CompressionStream(deflate) is not available in this browser`)
		}
	}

	async compressRows({width, height, rowReader}) {
		let expectedRowLength = width * 4
		let nextRowIndex = 0
		let sourceStream = new ReadableStream({
			pull: async (controller) => {
				if (nextRowIndex >= height) {
					controller.close()
					return
				}

				let rowData = await rowReader.getRow(nextRowIndex)
				this.validateRowData(rowData, nextRowIndex, expectedRowLength)
				controller.enqueue(this.buildScanline(rowData))
				nextRowIndex++
			}
		})
		let compressionStream = new CompressionStream("deflate")
		let compressedStream = sourceStream.pipeThrough(compressionStream)
		let compressedBuffer = await new Response(compressedStream).arrayBuffer()

		if (typeof rowReader.finish === "function") {
			await rowReader.finish()
		}

		return new Uint8Array(compressedBuffer)
	}

	validateRowData(rowData, rowIndex, expectedRowLength) {
		if (!(rowData instanceof Uint8Array)) {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} Row ${rowIndex} must be Uint8Array`)
		}

		if (rowData.length !== expectedRowLength) {
			throw new Error(`${GENOGRAM_EXPORT_DIAGNOSTIC_PREFIX} Row ${rowIndex} has invalid byte length ${rowData.length}, expected ${expectedRowLength}`)
		}
	}

	buildScanline(rowData) {
		let scanline = new Uint8Array(rowData.length + 1)
		scanline[0] = this.filterType
		scanline.set(rowData, 1)
		return scanline
	}
}
